// Third Party
import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { twMerge } from 'tailwind-merge';

// Utils
import { useLazyQuery } from '@apollo/client/react';
import { ReferralCodeFindByCodeDocument, type ReferralCodeDetails } from '@gql';

// UI
import { Button } from '@components/button';
import { Input } from '@components/form/input';
import { Typography } from '@components/typography';
import { Icon, IconNames } from '@components/icon';
export interface ReferralCodeInputProps {
  /** Initial referral code from URL or props */
  initialCode?: string;
  /** Callback when code changes (always called, even for invalid codes) */
  onCodeChange: (code: string) => void;
  /** Optional className */
  className?: string;
}
type ValidationState = {
  type: 'idle';
} | {
  type: 'validating';
} | {
  type: 'valid';
  data: ReferralCodeDetails;
} | {
  type: 'invalid';
  message: string;
};

// Animation variants
const inputVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    height: 0
  },
  visible: {
    opacity: 1,
    y: 0,
    height: 'auto' as const,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 25
    }
  },
  exit: {
    opacity: 0,
    y: -10,
    height: 0,
    transition: {
      duration: 0.2
    }
  }
};
const successCardVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 10
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 25
    }
  }
};
const shakeAnimation = {
  x: [0, -8, 8, -6, 6, -4, 4, 0],
  transition: {
    duration: 0.5
  }
};
export const ReferralCodeInput: React.FC<ReferralCodeInputProps> = ({
  initialCode,
  onCodeChange,
  className
}) => {
  const [isExpanded, setIsExpanded] = React.useState(!!initialCode);
  const [inputValue, setInputValue] = React.useState(initialCode || '');
  const [validationState, setValidationState] = React.useState<ValidationState>(initialCode ? {
    type: 'validating'
  } : {
    type: 'idle'
  });
  const [shouldShake, setShouldShake] = React.useState(false);
  const [validateCode, {
    loading: isValidating
  }] = useLazyQuery(ReferralCodeFindByCodeDocument);

  // Validate initial code when provided or when it changes
  React.useEffect(() => {
    if (initialCode) {
      validateCode({
        variables: {
          code: initialCode
        }
      }).then(queryResult => {
        const data = queryResult.data;
        if (data) {
          const result = data.referralCodeFindByCode;
          if (result.__typename === 'ReferralCodeDetails') {
            setValidationState({
              type: 'valid',
              data: result
            });
            onCodeChange(initialCode);
          } else {
            setValidationState({
              type: 'invalid',
              message: result.message || 'Code not recognized'
            });
            onCodeChange(initialCode);
          }
        }
      }).catch(() => {
        setValidationState({
          type: 'invalid',
          message: 'Unable to validate code'
        });
        onCodeChange(initialCode);
      });
    }
  }, [initialCode, validateCode, onCodeChange]);

  // Sync local state when initialCode prop changes
  React.useEffect(() => {
    setInputValue(initialCode || '');
    setIsExpanded(!!initialCode);
    setValidationState(initialCode ? {
      type: 'validating'
    } : {
      type: 'idle'
    });
  }, [initialCode]);
  const handleApply = async () => {
    const code = inputValue.trim().toUpperCase();
    if (!code) {
      setValidationState({
        type: 'idle'
      });
      onCodeChange('');
      return;
    }
    setValidationState({
      type: 'validating'
    });
    onCodeChange(code);
    try {
      const result_0 = await validateCode({
        variables: {
          code
        }
      });
      const response = result_0.data?.referralCodeFindByCode;
      if (response?.__typename === 'ReferralCodeDetails') {
        setValidationState({
          type: 'valid',
          data: response
        });
      } else {
        setValidationState({
          type: 'invalid',
          message: response?.message || 'Code not recognized'
        });
        setShouldShake(true);
        setTimeout(() => setShouldShake(false), 500);
      }
    } catch (error) {
      setValidationState({
        type: 'invalid',
        message: 'Unable to validate code'
      });
      setShouldShake(true);
      setTimeout(() => setShouldShake(false), 500);
    }
  };
  const handleRemove = () => {
    setInputValue('');
    setValidationState({
      type: 'idle'
    });
    setIsExpanded(false);
    onCodeChange('');
  };
  const formatRewardAmount = (cents: number): string => {
    return `$${(cents / 100).toFixed(0)}`;
  };
  return <div className={twMerge('space-y-2', className)} role="group" aria-labelledby="referral-code-label">
      {/* Screen reader label */}
      <span id="referral-code-label" className="sr-only">
        Referral code section
      </span>

      {/* Live region for state announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {validationState.type === 'validating' && 'Validating referral code...'}
        {validationState.type === 'valid' && `Referral code ${validationState.data.code} applied. You will receive ${formatRewardAmount(validationState.data.referredUserRewardAmountCents)}.`}
        {validationState.type === 'invalid' && validationState.message}
      </div>

      <AnimatePresence mode="wait">
        {/* Collapsed state - show enhanced trigger button */}
        {!isExpanded && validationState.type !== 'valid' && <motion.div key="collapsed" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="text-center">
            <motion.button type="button" onClick={() => setIsExpanded(true)} className="group inline-flex items-center gap-2 px-4 py-2.5
                         bg-pool/30 hover:bg-pool/50
                         rounded-lg border border-pool
                         transition-colors duration-200" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} aria-expanded={false} aria-label="Enter a referral code to receive a discount">
              <Icon name={IconNames.GIFT} className="text-primary-main w-4 h-4" />
              <span className="text-sm font-medium text-primary-main">
                Have a referral code?
              </span>
              <Icon name={IconNames.CHEVRON_RIGHT} className="text-primary-main w-3 h-3
                           group-hover:translate-x-0.5 transition-transform" />
            </motion.button>
          </motion.div>}

        {/* Expanded input state */}
        {isExpanded && validationState.type !== 'valid' && <motion.div key="input" variants={inputVariants} initial="hidden" animate="visible" exit="exit" className="space-y-2">
            <motion.div className="flex gap-2" animate={shouldShake ? shakeAnimation : {}}>
              <div className="flex-1">
                <Input type="text" value={inputValue} onChange={e => setInputValue(e.target.value.toUpperCase())} placeholder="Enter referral code" aria-label="Referral code" aria-describedby={validationState.type === 'invalid' ? 'referral-error' : undefined} aria-invalid={validationState.type === 'invalid'} isInvalid={validationState.type === 'invalid'} disabled={isValidating || validationState.type === 'validating'} onKeyDown={e_0 => {
              if (e_0.key === 'Enter') {
                e_0.preventDefault();
                handleApply();
              }
            }} className="text-sm uppercase tracking-wider" />
              </div>
              <Button type="button" onClick={handleApply} disabled={isValidating || validationState.type === 'validating' || !inputValue.trim()} isLoading={isValidating || validationState.type === 'validating'} className="border" size="md" variant="filled">
                Apply
              </Button>
            </motion.div>

            {/* Error message */}
            <AnimatePresence>
              {validationState.type === 'invalid' && <motion.div initial={{
            opacity: 0,
            y: -5,
            height: 0
          }} animate={{
            opacity: 1,
            y: 0,
            height: 'auto'
          }} exit={{
            opacity: 0,
            y: -5,
            height: 0
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} id="referral-error" role="alert" aria-live="assertive" className="flex items-start gap-2 p-2
                             bg-error-main/5 rounded-lg border border-error-main/20">
                  <Icon name={IconNames.CIRCLE_EXCLAMATION} solid className="text-error-main w-4 h-4 flex-shrink-0 mt-0.5" />
                  <Typography component="p" className="text-xs text-error-main font-medium mt-0.5">
                    {validationState.message}
                  </Typography>
                </motion.div>}
            </AnimatePresence>
          </motion.div>}

        {/* Success state with celebration */}
        {validationState.type === 'valid' && <motion.div key="success" variants={successCardVariants} initial="hidden" animate="visible" className="relative overflow-hidden rounded-xl bg-gradient-to-r from-pool/40 to-pool/20
                       border border-pool p-4 shadow-sm" role="status" aria-live="polite" aria-label={`Referral code ${validationState.data.code} applied successfully`}>
            {/* Subtle animated background */}
            <motion.div className="absolute inset-0 opacity-20" initial={{
          backgroundPosition: '0% 50%'
        }} animate={{
          backgroundPosition: '100% 50%'
        }} transition={{
          duration: 3,
          repeat: Infinity,
          repeatType: 'reverse'
        }} style={{
          background: 'linear-gradient(90deg, transparent, rgba(195, 226, 219, 0.5), transparent)',
          backgroundSize: '200% 100%'
        }} />

            <div className="relative flex items-start gap-3">
              {/* Animated checkmark */}
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 15,
            delay: 0.2
          }} className="flex-shrink-0 w-10 h-10 rounded-full bg-success-main/10
                           flex items-center justify-center">
                <Icon name={IconNames.CIRCLE_CHECK} solid className="text-success-main w-6 h-6" />
              </motion.div>

              <div className="flex-1 min-w-0">
                <motion.div initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }}>
                  <Typography component="p" className="text-sm font-medium text-primary-main">
                    Code applied successfully!
                  </Typography>
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.4
            }} className="mt-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center px-2 py-0.5
                                 bg-primary-main text-white rounded text-xs font-medium
                                 uppercase tracking-wide">
                      {validationState.data.code}
                    </span>
                    <span className="text-xs text-primary-main/70">
                      {validationState.data.campaignName}
                    </span>
                  </div>
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className="mt-2">
                  <div className="flex items-center gap-1.5">
                    <Icon name={IconNames.GIFT} solid className="text-secondary-main w-4 h-4" />
                    <Typography component="span" className="text-sm font-bold text-secondary-dark">
                      {`${formatRewardAmount(validationState.data.referredUserRewardAmountCents)} Cash Back Reward`}
                    </Typography>
                  </div>
                </motion.div>
              </div>

              {/* Remove button */}
              <motion.button type="button" onClick={handleRemove} className="flex-shrink-0 w-8 h-8 rounded-full
                           flex items-center justify-center
                           text-primary-main/60 hover:text-primary-main
                           hover:bg-primary-main/10
                           transition-colors" whileHover={{
            scale: 1.1
          }} whileTap={{
            scale: 0.9
          }} aria-label="Remove referral code">
                <Icon name={IconNames.X} className="w-4 h-4" />
              </motion.button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
ReferralCodeInput.displayName = 'ReferralCodeInput';